import React, { useState } from 'react';
import { Paper, TextField, Grid, Box, Button, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';

const MenuTab = ({ leadingContent }) => {
  const { t } = useTranslation();
  const { config, handleChange, isFieldConfigurable, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);

  const menuPath = 'siteConfig.menu';
  const isTitleConfigurable = isFieldConfigurable(`${menuPath}.title`);
  const isSubtitleConfigurable = isFieldConfigurable(`${menuPath}.subtitle`);
  const isDescriptionConfigurable = isFieldConfigurable(`${menuPath}.description`);
  const isEmptyStateTextConfigurable = isFieldConfigurable(`${menuPath}.emptyStateText`);
  const isCategoriesConfigurable = isFieldConfigurable(`${menuPath}.categories`);

  const hasEditableFields =
    isTitleConfigurable ||
    isSubtitleConfigurable ||
    isDescriptionConfigurable ||
    isEmptyStateTextConfigurable ||
    isCategoriesConfigurable;

  if (!hasEditableFields) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  const handleSave = async () => {
    try {
      setSaving(true);
      const updates = {};
      if (isTitleConfigurable) updates[`${menuPath}.title`] = config.siteConfig?.menu?.title ?? '';
      if (isSubtitleConfigurable) updates[`${menuPath}.subtitle`] = config.siteConfig?.menu?.subtitle ?? '';
      if (isDescriptionConfigurable) updates[`${menuPath}.description`] = config.siteConfig?.menu?.description ?? '';
      if (isEmptyStateTextConfigurable) updates[`${menuPath}.emptyStateText`] = config.siteConfig?.menu?.emptyStateText ?? '';
      if (isCategoriesConfigurable) updates[`${menuPath}.categories`] = config.siteConfig?.menu?.categories ?? [];
      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables');
        return;
      }
      await SitesService.updateFields(prepareFieldUpdates(updates));
      await loadConfig();
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving menu:', err);
      SnackbarUtils.error(err.response?.data?.message || err.message || (t('error_saving_section') || 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const menu = config.siteConfig?.menu ?? { title: '', subtitle: '', description: '', emptyStateText: '', categories: [] };

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      {leadingContent}
      <Grid container spacing={2}>
        {isTitleConfigurable && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Título del menú"
              value={menu.title || ''}
              onChange={(e) => handleChange('siteConfig.menu.title', e.target.value)}
            />
          </Grid>
        )}
        {isSubtitleConfigurable && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Subtítulo del menú"
              value={menu.subtitle || ''}
              onChange={(e) => handleChange('siteConfig.menu.subtitle', e.target.value)}
            />
          </Grid>
        )}
        {isDescriptionConfigurable && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Descripción del menú"
              value={menu.description || ''}
              onChange={(e) => handleChange('siteConfig.menu.description', e.target.value)}
            />
          </Grid>
        )}
        {isEmptyStateTextConfigurable && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Texto sin productos"
              value={menu.emptyStateText || ''}
              onChange={(e) => handleChange('siteConfig.menu.emptyStateText', e.target.value)}
            />
          </Grid>
        )}
        {isCategoriesConfigurable && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('menu_categories_info') || 'Las categorías y productos del menú se gestionan desde la configuración del sitio. Los cambios en título y subtítulo se guardan aquí.'}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
              {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default MenuTab;
